import { useEffect, useRef, useState } from 'react';

import { App, Segmented, Select, theme } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import {
  LEAD_TAG_FILTER_MAX,
  LeadTagMatch,
  LeadTagWithCount,
} from 'models/Lead';

import { LeadService } from 'services/LeadService';

import appI18n from '../../../config/i18n';

const I18N = 'scenes.leads.scene.filters';
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 50;

interface LeadTagsFilterProps {
  /** Country whose tag catalog is searched (the draft country). */
  country: string;
  /** Load only while the filters popover is open. */
  active: boolean;
  value: number[];
  /** Without a match handler the any/all control is hidden. */
  match?: LeadTagMatch;
  onChange: (ids: number[]) => void;
  onMatchChange?: (match: LeadTagMatch) => void;
  /** Bold label, to sit among the export modal's labelled fields. */
  strongLabel?: boolean;
  /** Class for the label, to match the host form's field labels. */
  labelClassName?: string;
  /**
   * The count covers leads in every status; hide it where the host lists only
   * some of them and the number would not match.
   */
  showLeadCount?: boolean;
}

/**
 * "Etiquetas" field of the filters popover. Search is a debounced server `q`
 * (the catalog can exceed one page, so filtering the loaded list would hide
 * tags); chosen tags are remembered so their labels survive new searches.
 */
function LeadTagsFilter({
  country,
  active,
  value,
  match,
  onChange,
  onMatchChange,
  strongLabel = false,
  labelClassName,
  showLeadCount = true,
}: LeadTagsFilterProps) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const { message } = App.useApp();
  const { token } = theme.useToken();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LeadTagWithCount[]>([]);
  const [loading, setLoading] = useState(false);
  const seenRef = useRef<Map<number, LeadTagWithCount>>(new Map());

  // Tag ids belong to one country: start over when it changes.
  useEffect(() => {
    seenRef.current = new Map();
    setResults([]);
    setQuery('');
  }, [country]);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const timer = setTimeout(
      async () => {
        setLoading(true);
        try {
          const response = await LeadService.getTags(
            country,
            query,
            SEARCH_LIMIT
          );
          if (cancelled) return;
          if (response.success) {
            const data = (response.data as LeadTagWithCount[]) ?? [];
            data.forEach((tag) => seenRef.current.set(tag.id, tag));
            setResults(data);
          } else {
            const error = response.data as ApiErrorData;
            message.error(
              (error.message as string) ||
                t('scenes.leads.scene.errors.fetch_tags'),
              3.5
            );
          }
        } catch (err) {
          if (!cancelled) {
            message.error(t('scenes.leads.scene.errors.fetch_tags'), 3.5);
          }
        } finally {
          // A superseded request must not leave the spinner on: the cleanup
          // below already reset it, and the newer request owns the flag.
          if (!cancelled) setLoading(false);
        }
      },
      query ? SEARCH_DEBOUNCE_MS : 0
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
      // Closing the popover (or a new query) abandons the in-flight request.
      setLoading(false);
    };
  }, [active, country, query]);

  const toOption = (tag: LeadTagWithCount) => ({
    value: tag.id,
    tagLabel: tag.name,
    label: (
      <span
        style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
      >
        <span>{tag.name}</span>
        {showLeadCount && (
          <span
            style={{
              color: token.colorTextTertiary,
              fontSize: 12,
              marginRight: 8,
            }}
          >
            {t('scenes.leads.components.lead_tags.lead_count', {
              count: tag.leadCount,
            })}
          </span>
        )}
      </span>
    ),
  });

  const selected = value
    .map((id) => seenRef.current.get(id))
    .filter((tag): tag is LeadTagWithCount => !!tag);
  const options = [
    ...selected,
    ...results.filter((tag) => !value.includes(tag.id)),
  ].map(toOption);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          // A host label class brings its own spacing.
          marginBottom: labelClassName ? 0 : 8,
        }}
      >
        <span
          className={labelClassName}
          style={strongLabel ? { fontWeight: 600 } : undefined}
        >
          {t(`${I18N}.tags_label`)}
        </span>
        {onMatchChange && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: token.colorTextTertiary,
            }}
          >
            {t(`${I18N}.tags_match_label`)}
            <Segmented
              size="small"
              value={match}
              onChange={(v) => onMatchChange(v as LeadTagMatch)}
              options={[
                { value: 'any', label: t(`${I18N}.tags_match_any`) },
                { value: 'all', label: t(`${I18N}.tags_match_all`) },
              ]}
            />
          </span>
        )}
      </div>
      <Select
        mode="multiple"
        showSearch
        filterOption={false}
        searchValue={query}
        onSearch={setQuery}
        value={value}
        onChange={(ids) => onChange(ids as number[])}
        options={options}
        optionLabelProp="tagLabel"
        loading={loading}
        style={{ width: '100%' }}
        placeholder={t(`${I18N}.tags_placeholder`)}
        allowClear
        maxCount={LEAD_TAG_FILTER_MAX}
        maxTagCount="responsive"
      />
    </div>
  );
}

export default LeadTagsFilter;
